import { useCallback, useState } from 'react';

import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { ConstructorApi } from '@/features/constructor/api';
import { queryClient } from '@/app/QueryClientProvider';
import { modifyAnswers } from '@/features/constructorCommon/utils';
import { usePopups } from '@/hooks';

export const useQuestionnaireSection = ({ questionSectionTypeId }) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = useCallback(async ({ section, dAnswersProp }) => {
    try {
      setIsCreating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : section.visibleConditions;
      const result = await ConstructorApi.createSection(
        { ...section, visibleConditions },
        questionSectionTypeId
      );
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionSectionTypeId
            })
        });
        showSuccessToast('Created');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsCreating(false);
    }
  }, []);

  const handleUpdate = useCallback(async ({ section, dAnswersProp }) => {
    try {
      setIsUpdating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : section.visibleConditions;
      const result = await ConstructorApi.updateSection({ ...section, visibleConditions });
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionSectionTypeId
            })
        });
        showSuccessToast('Saved');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (section) => {
    try {
      setIsDeleting(true);
      const result = await ConstructorApi.deleteSection(section);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionSectionTypeId
            })
        });
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    handleCreate,
    handleUpdate,
    handleDelete,
    isCreating,
    isUpdating,
    isDeleting
  };
};
